import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, TextField, InputAdornment } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { useRecoilValue } from "recoil";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import MightyDataGrid from "../../../../components/organisms/MightyDataGrid";
import useDataGridActions from "../../../../hooks/useDataGridActions";
import organizationIdSelector from "../../../../atoms/organizationIdSelector";

const RecipesDataGrid = ({ recipes, onClick, onEdit, onDelete, onUpdate }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const organizationId = useRecoilValue(organizationIdSelector);
  const { createEditDeleteActions } = useDataGridActions();
  const [searchTerm, setSearchTerm] = useState("");

  // Handle sharing recipe with public check
  const handleRecipeShare = async (recipe) => {
    if (!recipe.isPublic) {
      // Ask if they want to make it public
      confirmAlert({
        title: "Make Recipe Public?",
        message: `"${recipe.name}" is currently private. Do you want to make it public so it can be shared?`,
        buttons: [
          {
            label: "Yes, Make Public & Share",
            onClick: async () => {
              try {
                // Update recipe to public
                await onUpdate({
                  ...recipe,
                  isPublic: true,
                });
                // Copy URL after making public
                copyRecipeUrl({ ...recipe, isPublic: true });
              } catch (error) {
                console.error("Failed to make recipe public:", error);
                alert("Failed to make recipe public. Please try again.");
              }
            },
          },
          {
            label: "Cancel",
            onClick: () => {},
          },
        ],
      });
    } else {
      // Already public, just copy the URL
      copyRecipeUrl(recipe);
    }
  };

  // Copy public recipe URL to clipboard
  const copyRecipeUrl = async (recipe) => {
    try {
      const baseUrl = window.location.origin;
      const publicRecipeUrl = `${baseUrl}/recipe/${organizationId}/${recipe.id}`;

      await navigator.clipboard.writeText(publicRecipeUrl);

      // Show a brief notification
      const notification = document.createElement("div");
      notification.textContent = "Recipe URL copied to clipboard!";
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${theme.palette.primary.main};
        color: white;
        padding: 12px 20px;
        border-radius: 4px;
        z-index: 9999;
        font-family: Arial, sans-serif;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      `;
      document.body.appendChild(notification);

      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);
    } catch (error) {
      console.error("Failed to copy URL:", error);
      // Fallback: show the URL in an alert
      const baseUrl = window.location.origin;
      const publicRecipeUrl = `${baseUrl}/recipe/${organizationId}/${recipe.id}`;
      alert(`Copy this URL: ${publicRecipeUrl}`);
    }
  };

  // Create custom actions for recipes
  const createRecipeActions = (recipe) => {
    const actions = [
      ...createEditDeleteActions({
        onEdit: () => onEdit(recipe),
        onDelete: () => onDelete(recipe),
        entityName: "Recipe",
        pageName: "Nutrition",
        tabName: "Recipes",
      }),
    ];

    // Always add share action
    actions.splice(1, 0, {
      key: "share",
      label: "Share",
      icon: <ShareIcon fontSize="small" />,
      onClick: () => handleRecipeShare(recipe),
      show: true,
    });

    return actions;
  };

  const columns = React.useMemo(() => {
    return [
      {
        field: "name",
        headerName: "Name",
        minWidth: 100,
        flex: 1,
        hideable: false,
      },
      {
        field: "mealType",
        headerName: "Meal Type",
        headerAlign: "center",
        align: "center",
        width: 125,
        renderCell: (params) => {
          return t(params.value);
        },
      },
      {
        field: "isPublic",
        headerName: "Public?",
        type: "boolean",
        headerAlign: "center",
        align: "center",
        width: 85,
        renderCell: (params) => {
          if (!params.value) {
            return <Box>No</Box>;
          }

          return <Box style={{ color: theme.palette.secondary.main }}>Yes</Box>;
        },
      },
      {
        field: "ingredients",
        headerName: "Ingredients",
        headerAlign: "center",
        align: "center",
        width: 100,
        valueGetter: (params) => {
          return params.row.ingredients.length;
        },
      },
      {
        field: "servings",
        headerName: "Serves",
        width: 90,
        type: "number",
        headerAlign: "center",
        align: "center",
      },
      {
        field: "kCals",
        headerName: "Calories",
        width: 90,
        type: "number",
        headerAlign: "center",
        align: "center",
      },
      {
        field: "proteins",
        headerAlign: "center",
        align: "center",
        headerName: "Proteins (g)",
        width: 90,
        type: "number",
      },
      {
        field: "carbs",
        headerName: "Carbs (g)",
        width: 90,
        type: "number",
        headerAlign: "center",
        align: "center",
      },
      {
        field: "fats",
        headerName: "Fats (g)",
        width: 90,
        type: "number",
        headerAlign: "center",
        align: "center",
      },
      {
        field: "fiber",
        headerName: "Fiber (g)",
        width: 90,
        type: "number",
        headerAlign: "center",
        align: "center",
      },
    ];
  }, [t, theme.palette.secondary.main]);

  // Filter recipes based on search term
  const filteredRecipes = useMemo(() => {
    if (!searchTerm) return recipes;

    return recipes.filter((recipe) =>
      recipe.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [recipes, searchTerm]);

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <TextField
          placeholder="Search recipes by name..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
      </Box>
      <MightyDataGrid
        entityName="recipes"
        columns={columns}
        rows={filteredRecipes}
        onRowClick={onClick}
        showActions={true}
        actions={createRecipeActions}
      />
    </Box>
  );
};

export default RecipesDataGrid;
